import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Edit2, Mail, Copy, Info } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  Modal,
  Select,
  useToast,
  Textarea,
} from '@/components/ui'
import type { ColumnDef } from '@tanstack/react-table'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface EmailTemplate {
  id: string
  name: string
  type: string
  subject: string
  bodyText: string | null
  bodyHtml: string | null
  createdAt: string
  updatedAt: string
}

// ─── Template types with their available variables ────────────────────────────

const TEMPLATE_TYPES: {
  value: string
  label: string
  variables: { key: string; description: string }[]
}[] = [
  {
    value: 'billing',
    label: 'Abrechnung',
    variables: [
      { key: '{{customer_name}}', description: 'Kundenname' },
      { key: '{{invoice_number}}', description: 'Rechnungsnummer' },
      { key: '{{invoice_amount}}', description: 'Rechnungsbetrag' },
      { key: '{{due_date}}', description: 'Fälligkeitsdatum' },
      { key: '{{billing_period}}', description: 'Abrechnungszeitraum' },
      { key: '{{company_name}}', description: 'Firmenname (Absender)' },
    ],
  },
  {
    value: 'contract',
    label: 'Vertrag',
    variables: [
      { key: '{{customer_name}}', description: 'Kundenname' },
      { key: '{{contract_name}}', description: 'Vertragsname' },
      { key: '{{contract_start}}', description: 'Vertragsbeginn' },
      { key: '{{contract_end}}', description: 'Vertragsende' },
      { key: '{{company_name}}', description: 'Firmenname (Absender)' },
    ],
  },
  {
    value: 'reminder',
    label: 'Zahlungserinnerung',
    variables: [
      { key: '{{customer_name}}', description: 'Kundenname' },
      { key: '{{amount_due}}', description: 'Offener Betrag' },
      { key: '{{due_date}}', description: 'Fälligkeitsdatum' },
      { key: '{{invoice_number}}', description: 'Rechnungsnummer' },
      { key: '{{company_name}}', description: 'Firmenname (Absender)' },
    ],
  },
  {
    value: 'welcome',
    label: 'Willkommens-E-Mail',
    variables: [
      { key: '{{customer_name}}', description: 'Kundenname' },
      { key: '{{contact_name}}', description: 'Ansprechpartner' },
      { key: '{{company_name}}', description: 'Firmenname (Absender)' },
    ],
  },
  {
    value: 'quote',
    label: 'Angebot',
    variables: [
      { key: '{{customer_name}}', description: 'Kundenname' },
      { key: '{{quote_number}}', description: 'Angebotsnummer' },
      { key: '{{quote_total}}', description: 'Gesamtbetrag' },
      { key: '{{valid_until}}', description: 'Gültig bis' },
      { key: '{{company_name}}', description: 'Firmenname (Absender)' },
    ],
  },
  {
    value: 'custom',
    label: 'Benutzerdefiniert',
    variables: [
      { key: '{{customer_name}}', description: 'Kundenname' },
      { key: '{{company_name}}', description: 'Firmenname (Absender)' },
    ],
  },
]

function getTypeLabel(type: string): string {
  return TEMPLATE_TYPES.find((t) => t.value === type)?.label ?? type
}

function getTypeVariables(type: string) {
  return TEMPLATE_TYPES.find((t) => t.value === type)?.variables ?? []
}

// ─── Form State ───────────────────────────────────────────────────────────────

interface FormState {
  name: string
  type: string
  subject: string
  bodyText: string
  bodyHtml: string
}

const DEFAULT_FORM: FormState = {
  name: '',
  type: 'billing',
  subject: '',
  bodyText: '',
  bodyHtml: '',
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function EmailTemplatesTab() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [editTarget, setEditTarget] = useState<EmailTemplate | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<EmailTemplate | null>(null)
  const [form, setForm] = useState<FormState>(DEFAULT_FORM)
  const [activeBodyTab, setActiveBodyTab] = useState<'text' | 'html'>('text')

  const { data: templates = [], isLoading, error } = useQuery<EmailTemplate[]>({
    queryKey: ['email-templates'],
    queryFn: async () => {
      const { data } = await apiClient.get('/settings/email-templates')
      return data
    },
  })

  const createMutation = useMutation({
    mutationFn: async (payload: FormState) => {
      const { data } = await apiClient.post('/settings/email-templates', {
        name: payload.name.trim(),
        type: payload.type,
        subject: payload.subject.trim(),
        bodyText: payload.bodyText.trim() || null,
        bodyHtml: payload.bodyHtml.trim() || null,
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      setShowCreate(false)
      setForm(DEFAULT_FORM)
      toast({ title: 'E-Mail-Vorlage angelegt', tone: 'success' })
    },
    onError: (err: any) => {
      toast({ title: err?.response?.data?.message ?? 'Fehler beim Anlegen', tone: 'danger' })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: FormState }) => {
      const { data } = await apiClient.patch(`/settings/email-templates/${id}`, {
        name: payload.name.trim(),
        type: payload.type,
        subject: payload.subject.trim(),
        bodyText: payload.bodyText.trim() || null,
        bodyHtml: payload.bodyHtml.trim() || null,
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      setEditTarget(null)
      toast({ title: 'Vorlage gespeichert', tone: 'success' })
    },
    onError: (err: any) => {
      toast({ title: err?.response?.data?.message ?? 'Fehler beim Speichern', tone: 'danger' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/settings/email-templates/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      setDeleteTarget(null)
      toast({ title: 'Vorlage gelöscht', tone: 'success' })
    },
    onError: (err: any) => {
      toast({ title: err?.response?.data?.message ?? 'Fehler beim Löschen', tone: 'danger' })
    },
  })

  function openCreate() {
    setForm(DEFAULT_FORM)
    setActiveBodyTab('text')
    setShowCreate(true)
  }

  function openEdit(tpl: EmailTemplate) {
    setForm({
      name: tpl.name,
      type: tpl.type,
      subject: tpl.subject,
      bodyText: tpl.bodyText ?? '',
      bodyHtml: tpl.bodyHtml ?? '',
    })
    setActiveBodyTab('text')
    setEditTarget(tpl)
  }

  function handleSubmitCreate() {
    if (!form.name.trim()) {
      toast({ title: 'Name ist erforderlich', tone: 'danger' })
      return
    }
    createMutation.mutate(form)
  }

  function handleSubmitEdit() {
    if (!editTarget) return
    if (!form.name.trim()) {
      toast({ title: 'Name ist erforderlich', tone: 'danger' })
      return
    }
    updateMutation.mutate({ id: editTarget.id, payload: form })
  }

  const columns: ColumnDef<EmailTemplate>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <span className="font-medium text-gray-900">{row.original.name}</span>
      ),
    },
    {
      accessorKey: 'type',
      header: 'Typ',
      cell: ({ row }) => (
        <span className="text-sm text-gray-600">{getTypeLabel(row.original.type)}</span>
      ),
    },
    {
      accessorKey: 'subject',
      header: 'Betreff',
      cell: ({ row }) => (
        <span className="text-sm text-gray-500 truncate max-w-xs block">
          {row.original.subject || <span className="italic text-gray-400">Kein Betreff</span>}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex items-center gap-1 justify-end">
          <IconButton
            icon={<Edit2 className="w-3.5 h-3.5" />}
            aria-label="Bearbeiten"
            size="sm"
            onClick={() => openEdit(row.original)}
          />
          <IconButton
            icon={<Trash2 className="w-3.5 h-3.5" />}
            aria-label="Löschen"
            size="sm"
            variant="ghost"
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={() => setDeleteTarget(row.original)}
          />
        </div>
      ),
    },
  ]

  if (isLoading) return <LoadingState label="Lade E-Mail-Vorlagen …" />
  if (error) return <ErrorState title="Fehler beim Laden" description={String(error)} />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-h3 text-gray-900">E-Mail-Vorlagen</h2>
          <p className="text-body-sm text-gray-500 mt-0.5">
            Wiederverwendbare Vorlagen für automatisierte E-Mails. Variablen werden beim Versand durch echte Werte ersetzt.
          </p>
        </div>
        <Button leadingIcon={<Plus className="w-4 h-4" />} onClick={openCreate}>
          Vorlage anlegen
        </Button>
      </div>

      {templates.length === 0 ? (
        <EmptyState
          icon={<Mail />}
          title="Noch keine E-Mail-Vorlagen"
          description="Legen Sie Vorlagen für automatisierte E-Mails an, z. B. für Abrechnungen oder Vertragserinnerungen."
          action={
            <Button leadingIcon={<Plus className="w-4 h-4" />} onClick={openCreate}>
              Erste Vorlage anlegen
            </Button>
          }
        />
      ) : (
        <DataTable columns={columns} data={templates} />
      )}

      {/* Create Modal */}
      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="E-Mail-Vorlage anlegen"
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleSubmitCreate}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Speichern …' : 'Anlegen'}
            </Button>
          </div>
        }
      >
        <TemplateForm
          form={form}
          onChange={setForm}
          activeBodyTab={activeBodyTab}
          onBodyTabChange={setActiveBodyTab}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        title="E-Mail-Vorlage bearbeiten"
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setEditTarget(null)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleSubmitEdit}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Speichern …' : 'Speichern'}
            </Button>
          </div>
        }
      >
        <TemplateForm
          form={form}
          onChange={setForm}
          activeBodyTab={activeBodyTab}
          onBodyTabChange={setActiveBodyTab}
        />
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Vorlage löschen?"
        message={`Die Vorlage „${deleteTarget?.name}" wird dauerhaft gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.`}
        confirmLabel="Löschen"
        variant="danger"
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}

// ─── Template Form ─────────────────────────────────────────────────────────────

function TemplateForm({
  form,
  onChange,
  activeBodyTab,
  onBodyTabChange,
}: {
  form: FormState
  onChange: (f: FormState) => void
  activeBodyTab: 'text' | 'html'
  onBodyTabChange: (tab: 'text' | 'html') => void
}) {
  const variables = getTypeVariables(form.type)

  function copyVariable(key: string) {
    navigator.clipboard.writeText(key).catch(() => {})
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Name"
          value={form.name}
          onChange={(e) => onChange({ ...form, name: e.target.value })}
          placeholder="z. B. Abrechnungs-Benachrichtigung"
          required
        />
        <Select
          label="Typ"
          value={form.type}
          onValueChange={(v) => onChange({ ...form, type: v })}
          options={TEMPLATE_TYPES.map((t) => ({ value: t.value, label: t.label }))}
        />
      </div>

      <Input
        label="Betreff"
        value={form.subject}
        onChange={(e) => onChange({ ...form, subject: e.target.value })}
        placeholder="z. B. Ihre Rechnung {{invoice_number}} ist verfügbar"
        hint="Variablen können direkt im Betreff verwendet werden."
      />

      {/* Available variables */}
      {variables.length > 0 && (
        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
          <div className="flex items-center gap-1.5 mb-2 text-blue-700">
            <Info className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="text-xs font-medium">Verfügbare Variablen für Typ „{getTypeLabel(form.type)}"</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {variables.map((v) => (
              <button
                key={v.key}
                type="button"
                title={`${v.description} – Klicken zum Kopieren`}
                onClick={() => copyVariable(v.key)}
                className="flex items-center gap-1 px-2 py-0.5 rounded bg-white border border-blue-200 text-blue-700 text-xs font-mono hover:bg-blue-100 transition-colors"
              >
                <Copy className="w-2.5 h-2.5 opacity-60" />
                {v.key}
              </button>
            ))}
          </div>
          <p className="text-xs text-blue-600 mt-2">
            Klicken zum Kopieren. Variablen werden beim Versand automatisch durch echte Werte ersetzt.
          </p>
        </div>
      )}

      {/* Body tabs: Text / HTML */}
      <div>
        <div className="flex border-b border-gray-200 mb-3 gap-1">
          <button
            type="button"
            onClick={() => onBodyTabChange('text')}
            className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${
              activeBodyTab === 'text'
                ? 'border-brand-600 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Textinhalt
          </button>
          <button
            type="button"
            onClick={() => onBodyTabChange('html')}
            className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${
              activeBodyTab === 'html'
                ? 'border-brand-600 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            HTML-Inhalt
          </button>
        </div>

        {activeBodyTab === 'text' && (
          <Textarea
            label="Textinhalt"
            value={form.bodyText}
            onChange={(e) => onChange({ ...form, bodyText: e.target.value })}
            placeholder={`Guten Tag {{customer_name}},\n\nIhre Rechnung {{invoice_number}} steht bereit.\n\nMit freundlichen Grüßen\n{{company_name}}`}
            rows={10}
            hint="Klartext-Version für E-Mail-Clients ohne HTML-Unterstützung."
          />
        )}

        {activeBodyTab === 'html' && (
          <Textarea
            label="HTML-Inhalt"
            value={form.bodyHtml}
            onChange={(e) => onChange({ ...form, bodyHtml: e.target.value })}
            placeholder={`<p>Guten Tag <strong>{{customer_name}}</strong>,</p>\n<p>Ihre Rechnung <strong>{{invoice_number}}</strong> steht bereit.</p>`}
            rows={10}
            hint="HTML-Version der E-Mail. Variablen können auch hier verwendet werden."
            className="font-mono text-sm"
          />
        )}
      </div>
    </div>
  )
}
